import { useState, useEffect, useCallback } from 'react';

export interface PrinterInfo {
  name: string;
  displayName: string;
  isDefault: boolean;
}

// Extended ElectronAPI type for printer functionality
interface ElectronPrinterAPI {
  isElectron: boolean;
  device?: { getMachineId: () => Promise<string> };
  printer?: {
    getList: () => Promise<PrinterInfo[]>;
    printReceipt: (orderId: string, printerName?: string) => Promise<boolean>;
    printTest: (printerName: string) => Promise<boolean>;
    openCashDrawer: (printerName?: string) => Promise<boolean>;
  };
  cashDrawer?: {
    open: () => Promise<boolean>;
  };
  printReport?: (payload: { html: string; css?: string }) => Promise<void>;
}

// Check if running in Electron desktop app
const isElectron = () => {
  return typeof window !== 'undefined' && window.electronAPI !== undefined;
};

// Get typed electron API
const getElectronAPI = (): ElectronPrinterAPI | undefined => {
  if (typeof window !== 'undefined') {
    return window.electronAPI as ElectronPrinterAPI | undefined;
  }
  return undefined;
};

// Hook for managing printer settings
export function usePrinters() {
  const [printers, setPrinters] = useState<PrinterInfo[]>([]);
  const [defaultReceiptPrinter, setDefaultReceiptPrinter] = useState<string | null>(null);
  const [defaultKitchenPrinter, setDefaultKitchenPrinter] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDesktopApp, setIsDesktopApp] = useState(false);

  // Load printers on mount
  useEffect(() => {
    const loadPrinters = async () => {
      if (!isElectron()) {
        setIsDesktopApp(false);
        return;
      }

      setIsDesktopApp(true);
      setIsLoading(true);

      try {
        const printerList = await getElectronAPI()?.printer?.getList();
        setPrinters(printerList || []);

        // Load saved defaults from localStorage
        const savedReceiptPrinter = localStorage.getItem('blazepos_receipt_printer');
        const savedKitchenPrinter = localStorage.getItem('blazepos_kitchen_printer');

        if (savedReceiptPrinter) {
          setDefaultReceiptPrinter(savedReceiptPrinter);
        }
        if (savedKitchenPrinter) {
          setDefaultKitchenPrinter(savedKitchenPrinter);
        }
      } catch (error) {
        console.error('Failed to load printers:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPrinters();
  }, []);

  // Refresh printer list
  const refreshPrinters = useCallback(async () => {
    if (!isElectron()) return;

    setIsLoading(true);
    try {
      const printerList = await getElectronAPI()?.printer?.getList();
      setPrinters(printerList || []);
    } catch (error) {
      console.error('Failed to refresh printers:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Set default receipt printer
  const setReceiptPrinter = useCallback((printerName: string | null) => {
    setDefaultReceiptPrinter(printerName);
    if (printerName) {
      localStorage.setItem('blazepos_receipt_printer', printerName);
    } else {
      localStorage.removeItem('blazepos_receipt_printer');
    }
  }, []);

  // Set default kitchen printer
  const setKitchenPrinter = useCallback((printerName: string | null) => {
    setDefaultKitchenPrinter(printerName);
    if (printerName) {
      localStorage.setItem('blazepos_kitchen_printer', printerName);
    } else {
      localStorage.removeItem('blazepos_kitchen_printer');
    }
  }, []);

  // Print test page
  const printTestPage = useCallback(async (printerName: string) => {
    if (!isElectron()) {
      // Web fallback - print test via popup window
      const testHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Printer Test</title>
          <style>
            @page { size: 80mm auto; margin: 0; }
            body {
              font-family: 'Courier New', monospace;
              font-size: 12px;
              width: 80mm;
              padding: 10mm;
              text-align: center;
            }
            h1 { font-size: 16px; margin-bottom: 10px; }
            hr { border: 1px dashed #000; margin: 10px 0; }
          </style>
        </head>
        <body>
          <h1>BLAZEPOS TEST PRINT</h1>
          <hr>
          <p>Date: ${new Date().toLocaleString()}</p>
          <p>Printer: ${printerName || 'Default'}</p>
          <hr>
          <p>If you can read this, your printer is working correctly!</p>
          <hr>
          <p>*** END TEST ***</p>
        </body>
        </html>
      `;

      const printWindow = window.open('', '_blank', 'width=320,height=400');
      if (printWindow) {
        printWindow.document.write(testHtml);
        printWindow.document.close();
        printWindow.onload = () => {
          printWindow.focus();
          printWindow.print();
          setTimeout(() => printWindow.close(), 1000);
        };
      }
      return true;
    }

    try {
      return await getElectronAPI()?.printer?.printTest(printerName);
    } catch (error) {
      console.error('Failed to print test page:', error);
      return false;
    }
  }, []);

  // Open cash drawer
  const openCashDrawer = useCallback(async () => {
    if (!isElectron()) {
      console.warn('Cash drawer only available in desktop app');
      return false;
    }

    try {
      return await getElectronAPI()?.cashDrawer?.open();
    } catch (error) {
      console.error('Failed to open cash drawer:', error);
      return false;
    }
  }, []);

  return {
    printers,
    defaultReceiptPrinter,
    defaultKitchenPrinter,
    isLoading,
    isDesktopApp,
    refreshPrinters,
    setReceiptPrinter,
    setKitchenPrinter,
    printTestPage,
    openCashDrawer,
  };
}

